import React, { useState } from 'react';
import { ENERGY_TEST_EXPLANATIONS } from '../../utils/interpretTestData';

const TURBULENCE_COLORS = {
  Calm: '#22c55e',
  Choppy: '#eab308',
  Turbulent: '#f97316',
  Crisis: '#ef4444',
};

const REGIME_COLORS = {
  'Risk-On': '#22c55e',
  Neutral: '#eab308',
  'Risk-Off': '#ef4444',
};

const MacroEnergyPanel = ({
  standaloneEnergy,
  energyLoading,
  energyError,
  showStandaloneEnergy,
  fetchMacroEnergy,
  setShowStandaloneEnergy,
  selectedTest,
  setSelectedTest,
  getScoreColor,
  turbulence,
}) => {
  const [showEnergyTests, setShowEnergyTests] = useState(false);
  const [showTurbulenceSignals, setShowTurbulenceSignals] = useState(false);

  const regime = standaloneEnergy?.regime || 'Neutral';
  const energyScore = standaloneEnergy?.energy_score;
  const rbm = standaloneEnergy?.risk_budget_multiplier;
  const turbLabel = turbulence?.turbulence_label || 'Calm';
  const turbPct = turbulence ? Math.round(turbulence.turbulence_score * 100) : 0;
  const dampener = turbulence?.dampener ?? 1.0;

  return (
    <div className="standalone-energy-section">
      <div className="energy-header">
        <h3>Market Conditions</h3>
        <button
          className="energy-fetch-button"
          onClick={fetchMacroEnergy}
          disabled={energyLoading}
        >
          {energyLoading ? 'Analyzing...' : 'Run Analysis'}
        </button>
      </div>

      {energyError && (
        <div className="energy-error">{energyError}</div>
      )}

      {standaloneEnergy && showStandaloneEnergy && (
        <div className="standalone-energy-results">
          {/* ── Side-by-side summary cards ── */}
          <div className="macro-conditions-grid">
            {/* Energy Card */}
            <div className="macro-card">
              <div className="macro-card-label">Energy</div>
              <div className="macro-card-score" style={{ color: getScoreColor(energyScore, 5) }}>
                {energyScore?.toFixed(1)}<span className="macro-card-max">/5</span>
              </div>
              <div className="macro-card-tag" style={{
                color: REGIME_COLORS[regime] || '#eab308',
                borderColor: REGIME_COLORS[regime] || '#eab308',
              }}>
                {regime}
              </div>
              <div className="macro-card-detail">
                Risk Budget: {rbm?.toFixed(2)}x
              </div>
            </div>

            {/* Turbulence Card */}
            <div className="macro-card">
              <div className="macro-card-label">Turbulence</div>
              <div className="macro-card-score" style={{ color: TURBULENCE_COLORS[turbLabel] || '#eab308' }}>
                {turbPct}%
              </div>
              <div className="macro-card-tag" style={{
                color: TURBULENCE_COLORS[turbLabel] || '#eab308',
                borderColor: TURBULENCE_COLORS[turbLabel] || '#eab308',
              }}>
                {turbLabel}
              </div>
              <div className="macro-card-detail">
                Dampener: {(dampener * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          {/* ── Impact explanation ── */}
          <div className="macro-impact-text">
            <strong>How this affects scores:</strong>{' '}
            Quality is unaffected by market conditions. Setup and Convex scores are
            {dampener < 1.0
              ? ` reduced to ${(dampener * 100).toFixed(0)}% by turbulence dampening.`
              : ' at full conviction (no dampening).'}
            {rbm && rbm !== 1.0
              ? ` The risk budget multiplier (${rbm.toFixed(2)}x) ${rbm > 1 ? 'boosts' : 'reduces'} overall conviction.`
              : ''}
          </div>

          {/* ── Collapsible Energy Tests ── */}
          <div className="macro-drilldown">
            <button
              className="macro-drilldown-toggle"
              onClick={() => setShowEnergyTests(!showEnergyTests)}
            >
              <span>{showEnergyTests ? '▼' : '▶'} Energy Tests (6)</span>
            </button>
            {showEnergyTests && (
              <div className="energy-tests-grid standalone-grid">
                {standaloneEnergy.tests?.map((test) => {
                  const testScore = test.score ?? 0.5;
                  const isSelected = selectedTest?.name === test.name;
                  const testDataForInterpret = {
                    score_0to1: testScore,
                    raw: test.raw_data,
                    notes: test.interpretation,
                  };

                  return (
                    <div
                      key={test.name}
                      className={`energy-test-item standalone-test ${isSelected ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTest(isSelected ? null : { name: test.name, data: testDataForInterpret });
                      }}
                    >
                      <div className="test-header">
                        <span className="test-name">
                          {test.name.replace(/_/g, ' ')}
                          <span className="test-weight">({(test.weight * 100).toFixed(0)}%)</span>
                          <span className="info-icon">i</span>
                        </span>
                        <span className="test-score" style={{ color: getScoreColor(testScore * 100, 100) }}>
                          {(testScore * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="test-bar">
                        <div className="test-bar-fill" style={{
                          width: `${testScore * 100}%`,
                          backgroundColor: getScoreColor(testScore * 100, 100),
                        }} />
                      </div>
                      <div className="test-interpretation">{test.interpretation}</div>
                      {isSelected && (
                        <div className="test-explanation-popup standalone-popup">
                          <div className="test-explanation-header">
                            <h6>{ENERGY_TEST_EXPLANATIONS[test.name]?.title || test.name}</h6>
                            <button className="close-btn" onClick={(e) => { e.stopPropagation(); setSelectedTest(null); }}>x</button>
                          </div>
                          <p className="test-explanation-desc">
                            {ENERGY_TEST_EXPLANATIONS[test.name]?.description || 'No description available.'}
                          </p>
                          <div className="test-explanation-scoring">
                            <strong>How it's scored:</strong> {ENERGY_TEST_EXPLANATIONS[test.name]?.scoring || 'Score range 0-100%'}
                          </div>
                          <div className="test-explanation-interpretation">
                            <strong>Current reading:</strong>
                            <p className="interpretation-text">{test.interpretation}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Collapsible Turbulence Signals ── */}
          {turbulence?.signals && Object.keys(turbulence.signals).length > 0 && (
            <div className="macro-drilldown">
              <button
                className="macro-drilldown-toggle"
                onClick={() => setShowTurbulenceSignals(!showTurbulenceSignals)}
              >
                <span>{showTurbulenceSignals ? '▼' : '▶'} Turbulence Signals (5)</span>
              </button>
              {showTurbulenceSignals && (
                <div className="energy-tests-grid standalone-grid">
                  {Object.entries(turbulence.signals).map(([name, sig]) => {
                    const sigScore = sig.value ?? 0;
                    return (
                      <div key={name} className="energy-test-item standalone-test">
                        <div className="test-header">
                          <span className="test-name">
                            {name.replace(/_/g, ' ')}
                            <span className="test-weight">({(sig.weight * 100).toFixed(0)}%)</span>
                          </span>
                          <span className="test-score" style={{
                            color: sigScore >= 0.6 ? '#ef4444' : sigScore >= 0.3 ? '#eab308' : '#22c55e',
                          }}>
                            {(sigScore * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="test-bar">
                          <div className="test-bar-fill" style={{
                            width: `${sigScore * 100}%`,
                            backgroundColor: sigScore >= 0.6 ? '#ef4444' : sigScore >= 0.3 ? '#eab308' : '#22c55e',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Action guidance ── */}
          {turbulence?.action_guidance && (
            <div className="macro-action-guidance">
              {turbulence.action_guidance}
            </div>
          )}

          <button
            className="energy-collapse-button"
            onClick={() => setShowStandaloneEnergy(false)}
          >
            Collapse
          </button>
        </div>
      )}
    </div>
  );
};

export default MacroEnergyPanel;
